// src/pages/FullMapPage.js
import React, { useEffect, useRef, useState } from 'react';
import { firestore, collection, query, where, getDocs, onSnapshot, doc } from '../firebase-config';
import './FullMapPage.css';

const FullMapPage = () => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [blocks, setBlocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [mapMode, setMapMode] = useState('color'); // 'color' or 'heat'
  
  // FEATURE FLAG: Set to true to enable heat map mode
  const HEAT_MAP_ENABLED = false;
  
  // Camera state
  const [camera, setCamera] = useState({
    x: 0,
    z: 0,
    zoom: 1
  });

  // Map bounds - much larger than spawn
  const MAP_BOUNDS = {
    minX: -710,
    maxX: 710,
    minZ: -710,
    maxZ: 710
  };

  const PIXEL_SIZE = 4; // Base size before zoom
  const MAP_WIDTH = MAP_BOUNDS.maxX - MAP_BOUNDS.minX;
  const MAP_HEIGHT = MAP_BOUNDS.maxZ - MAP_BOUNDS.minZ;

  // Pan state
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, z: 0 });
  const [lastPaintCount, setLastPaintCount] = useState(0);

  // Load blocks in chunks to avoid overwhelming Firestore
  useEffect(() => {
    loadBlocks();
    
    // Set up real-time listener for new paints
    const lastPaintRef = doc(firestore, 'meta', 'lastPaint');
    const unsubscribe = onSnapshot(lastPaintRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        const key = `${data.x}_${data.z}`;
        
        // Update this specific block in real-time
        setBlocks(prev => ({
          ...prev,
          [key]: {
            x: data.x,
            z: data.z,
            hex: data.colourHex,
            colour: data.colour,
            heat: prev[key]?.heat || 0 // Keep existing heat or default to 0
          }
        }));
      }
    });

    return () => unsubscribe();
  }, []);

  const loadBlocks = async () => {
    setLoading(true);
    
    // Load in chunks to avoid hitting Firestore limits
    const chunkSize = 200;
    const allBlocks = {};
    
    for (let x = MAP_BOUNDS.minX; x < MAP_BOUNDS.maxX; x += chunkSize) {
      const maxX = Math.min(x + chunkSize, MAP_BOUNDS.maxX);
      
      const q = query(
        collection(firestore, 'blocks'),
        where('x', '>=', x),
        where('x', '<', maxX)
      );

      const snapshot = await getDocs(q);
      
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.z >= MAP_BOUNDS.minZ && data.z <= MAP_BOUNDS.maxZ) {
          const key = `${data.x}_${data.z}`;
          allBlocks[key] = {
            x: data.x,
            z: data.z,
            hex: data.hex || '#FFFFFF',
            colour: data.colour || 'WHITE',
            heat: data.heat || 0
          };
        }
      });
    }

    setBlocks(allBlocks);
    setLastPaintCount(Object.keys(allBlocks).length);
    setLoading(false);
  };

  // Get heat color for heat map mode
  const getHeatColor = (heat) => {
    if (heat === 0) return '#F0F0F0';
    
    const maxHeat = 50;
    const normalized = Math.min(heat / maxHeat, 1);

    if (normalized < 0.2) return interpolateColor('#0000FF', '#00FFFF', normalized / 0.2);
    if (normalized < 0.4) return interpolateColor('#00FFFF', '#00FF00', (normalized - 0.2) / 0.2);
    if (normalized < 0.6) return interpolateColor('#00FF00', '#FFFF00', (normalized - 0.4) / 0.2);
    if (normalized < 0.8) return interpolateColor('#FFFF00', '#FF8800', (normalized - 0.6) / 0.2);
    return interpolateColor('#FF8800', '#FF0000', (normalized - 0.8) / 0.2);
  };

  const interpolateColor = (color1, color2, factor) => {
    const c1 = parseInt(color1.slice(1), 16);
    const c2 = parseInt(color2.slice(1), 16);
    
    const r1 = (c1 >> 16) & 0xff;
    const g1 = (c1 >> 8) & 0xff;
    const b1 = c1 & 0xff;
    
    const r2 = (c2 >> 16) & 0xff;
    const g2 = (c2 >> 8) & 0xff;
    const b2 = c2 & 0xff;
    
    const r = Math.round(r1 + factor * (r2 - r1));
    const g = Math.round(g1 + factor * (g2 - g1));
    const b = Math.round(b1 + factor * (b2 - b1));
    
    return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
  };

  // Render canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || loading) return;

    const ctx = canvas.getContext('2d');
    const container = containerRef.current;
    
    canvas.width = container.clientWidth;
    canvas.height = container.clientHeight;

    // Clear canvas
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Calculate viewport
    const centerX = canvas.width / 2;
    const centerZ = canvas.height / 2;
    const scaledPixelSize = PIXEL_SIZE * camera.zoom;

    // Draw blocks
    Object.values(blocks).forEach(block => {
      // const screenX = centerX + (block.x - camera.x) * scaledPixelSize;
      // const screenZ = centerZ + (block.z - camera.z) * scaledPixelSize;
      const screenX = Math.round(centerX + (block.x - camera.x) * scaledPixelSize);
      const screenZ = Math.round(centerZ + (block.z - camera.z) * scaledPixelSize);

      // Only draw if on screen
      if (screenX < -scaledPixelSize || screenX > canvas.width ||
          screenZ < -scaledPixelSize || screenZ > canvas.height) {
        return;
      }

      ctx.fillStyle = mapMode === 'color' ? block.hex : getHeatColor(block.heat);
      ctx.fillRect(screenX, screenZ, Math.ceil(scaledPixelSize), Math.ceil(scaledPixelSize));
    });

    // Draw grid at high zoom
    // if (camera.zoom >= 2) {
    //   ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    //   ctx.lineWidth = 0.5;
      
    //   for (let x = -10; x <= 10; x++) {
    //     const screenX = centerX + (x - camera.x % 1) * scaledPixelSize;
    //     ctx.beginPath();
    //     ctx.moveTo(screenX, 0);
    //     ctx.lineTo(screenX, canvas.height);
    //     ctx.stroke();
    //   }
      
    //   for (let z = -10; z <= 10; z++) {
    //     const screenZ = centerZ + (z - camera.z % 1) * scaledPixelSize;
    //     ctx.beginPath();
    //     ctx.moveTo(0, screenZ);
    //     ctx.lineTo(canvas.width, screenZ);
    //     ctx.stroke();
    //   }
    // }

  }, [blocks, camera, loading, mapMode]);

  // Mouse handlers for panning
  const handleMouseDown = (e) => {
    setIsPanning(true);
    setPanStart({ x: e.clientX, z: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isPanning) return;

    const dx = e.clientX - panStart.x;
    const dz = e.clientY - panStart.z;

    setCamera(prev => ({
      ...prev,
      x: prev.x - dx / (PIXEL_SIZE * prev.zoom),
      z: prev.z - dz / (PIXEL_SIZE * prev.zoom)
    }));

    setPanStart({ x: e.clientX, z: e.clientY });
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newZoom = Math.max(0.5, Math.min(10, camera.zoom * delta));
    
    setCamera(prev => ({
      ...prev,
      zoom: newZoom
    }));
  };

  const handleCanvasClick = (e) => {
    // if (isPanning) return; // Don't show info if user was panning

    // const canvas = canvasRef.current;
    // const rect = canvas.getBoundingClientRect();
    // const clickX = e.clientX - rect.left;
    // const clickY = e.clientY - rect.top;

    // const centerX = canvas.width / 2;
    // const centerZ = canvas.height / 2;
    // const scaledPixelSize = PIXEL_SIZE * camera.zoom;

    // const blockX = Math.floor(camera.x + (clickX - centerX) / scaledPixelSize);
    // const blockZ = Math.floor(camera.z + (clickY - centerZ) / scaledPixelSize);
    // const key = `${blockX}_${blockZ}`;

    // const block = blocks[key];
    // if (block) {
    //   let alertMessage = `Block at (${blockX}, ${blockZ})\nColor: ${block.colour}`;
      
    //   // Only show heat info if heat map is enabled
    //   if (HEAT_MAP_ENABLED) {
    //     const heatLevel = 
    //       block.heat === 0 ? 'Cold (no activity)' :
    //       block.heat < 5 ? 'Cool' :
    //       block.heat < 10 ? 'Warm' :
    //       block.heat < 20 ? 'Hot' :
    //       'BLAZING';
    //     alertMessage += `\nHeat: ${block.heat} (${heatLevel})`;
    //   }

    //   alert(alertMessage);
    // } else {
    //   alert(`Block at (${blockX}, ${blockZ})\nNot painted yet`);
    // }
  };

  const resetCamera = () => {
    setCamera({ x: 0, z: 0, zoom: 1 });
  };

  return (
    <div className="container full-map-page">
      <div className="map-header">
        <h1>Full Canvas Map</h1>
        <div className="map-controls">
          {HEAT_MAP_ENABLED && (
            <div className="map-toggle-group">
              <button
                className={mapMode === 'color' ? 'mode-btn active' : 'mode-btn'}
                onClick={() => setMapMode('color')}
              >
                Colours
              </button>
              <button
                className={mapMode === 'heat' ? 'mode-btn active' : 'mode-btn'}
                onClick={() => setMapMode('heat')}
              >
                Heat Map
              </button>
            </div>
          )}
          <button className="reset-btn" onClick={resetCamera}>
            Reset View
          </button>
        </div>
      </div>

      <div className="map-info-bar">
        <span>{Object.keys(blocks).length.toLocaleString()} blocks painted</span>
        <span>Zoom: {camera.zoom.toFixed(1)}x</span>
        <span>Position: ({Math.round(camera.x)}, {Math.round(camera.z)})</span>
      </div>

      {loading ? (
        <div className="map-loading">
          <div className="loading-spinner"></div>
          <p>Loading canvas...</p>
        </div>
      ) : (
        <div 
          ref={containerRef}
          className="full-map-container"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
        >
          <canvas 
            ref={canvasRef}
            onClick={handleCanvasClick}
            style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
          />
        </div>
      )}

      <div className="map-instructions">
        <p>Click and drag to pan • Scroll to zoom • Click blocks for info</p>
      </div>
    </div>
  );
};

export default FullMapPage;